import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from '../../adminconfig';
const API_URL = `${API_BASE_URL}/api/blogs`;
const API_URLimage = `${API_BASE_URL}/uploads`;

const InsightsTable = () => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    author: "",
    publishDate: "",
    overview: "",
    type: "",
    image: null,
    sections: [{ title: "", description: "" }],
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [filterType, setFilterType] = useState("");

  const token = localStorage.getItem("token"); // ✅ Get token once

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      const res = await axios.get(API_URL);
      setInsights(res.data);
    } catch (err) {
      console.error("Error fetching insights:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this insight?")) {
      try {
        await axios.delete(`${API_URL}/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setInsights((prev) => prev.filter((insight) => insight._id !== id));
      } catch (error) {
        console.error("Error deleting insight:", error);
      }
    }
  };

  const handleEditClick = (insight) => {
    setFormData({
      title: insight.title || "",
      author: insight.author || "",
      publishDate: insight.publishDate
        ? insight.publishDate.split("T")[0]
        : "",
      overview: insight.overview || "",
      type: insight.type || "",
      image: null, // reset image on edit
      sections: insight.sections || [{ title: "", description: "" }],
    });
    setEditId(insight._id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleFilterChange = async (e) => {
    const selectedType = e.target.value;
    setFilterType(selectedType);

    if (selectedType === "") {
      fetchInsights();
    } else {
      try {
        const res = await axios.get(`${API_URL}/type/${selectedType}`);
        setInsights(res.data);
      } catch (err) {
        console.error("Error fetching filtered blogs:", err);
      }
    }
  };

  const handleImageChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      image: e.target.files[0],
    }));
  };

  const handleSectionChange = (index, e) => {
    const { name, value } = e.target;
    const updatedSections = [...formData.sections];
    updatedSections[index][name] = value;
    setFormData((prev) => ({ ...prev, sections: updatedSections }));
  };

  const addSection = () => {
    setFormData((prev) => ({
      ...prev,
      sections: [...prev.sections, { title: "", description: "" }],
    }));
  };

  const removeSection = (index) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (key === "sections") {
        payload.append(key, JSON.stringify(value));
      } else if (value) {
        payload.append(key, value);
      }
    });

    try {
      if (isEditing && editId) {
        // UPDATE
        const res = await axios.put(`${API_URL}/${editId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setInsights((prev) =>
          prev.map((i) => (i._id === editId ? res.data : i))
        );
      } else {
        // CREATE
        const res = await axios.post(API_URL, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setInsights((prev) => [...prev, res.data]);
      }
    } catch (err) {
      console.error("Error submitting form:", err.response?.data || err);
    }

    // Reset
    setFormData({
      title: "",
      author: "",
      publishDate: "",
      overview: "",
      type: "",
      image: null,
      sections: [{ title: "", description: "" }],
    });
    setIsEditing(false);
    setEditId(null);
    setShowForm(false);
  };

  if (loading) return <p className="text-center mt-4">Loading...</p>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-bold">Insights Table</h1>

        <div className="flex items-center gap-4">
          <div>
            <label className="block font-semibold mb-1">Filter by Type</label>
            <select
              value={filterType}
              onChange={handleFilterChange}
              className="border p-2 rounded"
            >
              <option value="">All</option>
              <option value="blogs">Blog</option>
              <option value="news">News</option>
              <option value="case-study">Case Study</option>
            </select>
          </div>

          <button
            className="bg-blue-500 text-white px-4 py-2 rounded h-fit mt-6"
            onClick={() => setShowForm(true)}
          >
            Add Content
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="table-auto border border-gray-300 w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-4 py-2">ID</th>
              <th className="border px-4 py-2">Title</th>
              <th className="border px-4 py-2">Type</th>
              <th className="border px-4 py-2">Author</th>
              <th className="border px-4 py-2">Date</th>
              <th className="border px-4 py-2">Image</th>
              <th className="border px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {insights.map((item) => (
              <tr key={item._id}>
                <td className="border px-4 py-2">{item._id}</td>
                <td className="border px-4 py-2">{item.title}</td>
                <td className="border px-4 py-2">{item.type}</td>
                <td className="border px-4 py-2">{item.author || "-"}</td>
                <td className="border px-4 py-2">
                  {item.publishDate
                    ? new Date(item.publishDate).toLocaleDateString()
                    : "-"}
                </td>
                <td className="border px-4 py-2">
                  {item.image ? (
                    <img
                      src={`${API_URLimage}/${item.image}`}
                      alt={item.title}
                      className="w-16 h-16 object-cover"
                    />
                  ) : (
                    "-"
                  )}
                </td>
                <td className="border px-4 py-2">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditClick(item)}
                      className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item._id)}
                      className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </td>

              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-[500px] max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold mb-4">
              {isEditing ? "Edit Insight" : "Add New Insight"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-3">
              <select
                name="type"
                value={formData.type}
                className="border w-full p-2"
                onChange={handleChange}
                required
              >
                <option value="">Select Type</option>
                <option value="news">News</option>
                <option value="blogs">Blog</option>
                <option value="case-study">Case Study</option>
              </select>
              <input
                type="text"
                name="title"
                placeholder="Title"
                value={formData.title}
                className="border w-full p-2"
                onChange={handleChange}
                required
              />
              <input
                type="text"
                name="author"
                placeholder="Author"
                value={formData.author}
                className="border w-full p-2"
                onChange={handleChange}
              />
              <input
                type="date"
                name="publishDate"
                value={formData.publishDate}
                className="border w-full p-2"
                onChange={handleChange}
              />
              <textarea
                name="overview"
                placeholder="Overview"
                value={formData.overview}
                className="border w-full p-2"
                onChange={handleChange}
              ></textarea>

              <input
                type="file"
                name="image"
                accept="image/*,.gif"
                className="border w-full p-2"
                onChange={handleImageChange}
              />
              {formData.image && (
                <img
                  src={
                    formData.image instanceof File
                      ? URL.createObjectURL(formData.image)
                      : formData.image
                  }
                  alt="Preview"
                  className="mt-2 w-32 h-32 object-cover border"
                />
              )}


              {/* Sections */}
              <div>
                <h3 className="font-semibold">Sections</h3>
                {formData.sections.map((sec, idx) => (
                  <div
                    key={idx}
                    className="space-y-2 mb-2 border p-3 rounded relative"
                  >
                    <input
                      type="text"
                      name="title"
                      placeholder="Section Title"
                      value={sec.title}
                      onChange={(e) => handleSectionChange(idx, e)}
                      className="border w-full p-2"
                    />
                    <textarea
                      name="description"
                      placeholder="Section Description"
                      value={sec.description}
                      onChange={(e) => handleSectionChange(idx, e)}
                      className="border w-full p-2"
                    ></textarea>

                    {formData.sections.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSection(idx)}
                        className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 text-sm rounded"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addSection}
                  className="bg-green-500 text-white px-2 py-1 rounded mt-2"
                >
                  + Add Section
                </button>
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="bg-gray-400 text-white px-4 py-2 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded"
                >
                  {isEditing ? "Update Insight" : "Add Insight"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsightsTable;
